import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, map, startWith, switchMap, tap, finalize, of } from 'rxjs';
import { HealthcareServicesService } from './healthcare-services.service';
import { HealthcareService } from 'fhir/r4';
import { HealthcareServiceEditorDialogComponent } from './healthcare-service-editor-dialog.component';
import { ErrorService } from '../shared/error.service';
import { AuthService } from '../shared/auth/auth.service';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { ChipsDirective } from '../chips.directive';
import { HealthcareServiceSearchService } from './healthcare-service-search.service';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'app-healthcare-services-v2',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ChipsDirective,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatInputModule,
    MatTableModule,
    MatProgressSpinnerModule,
    ReferenceDisplayDirective,
    TableHeaderComponent,
    MatPaginatorModule,
    MatDividerModule
  ],
  templateUrl: './healthcare-services-v2.component.html',
  styleUrl: './healthcare-services-v2.component.scss'
})
export class HealthcareServicesV2Component {
  private readonly service = inject(HealthcareServicesService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly errorService = inject(ErrorService);
  private readonly searchService = inject(HealthcareServiceSearchService);
  private readonly auth = inject(AuthService);

  displayedColumns = ['name', 'status', 'category', 'location', 'updated', 'actions'];
  isRefreshing = false;

  readonly dataSource = this.searchService.dataSource;
  readonly loading$ = this.searchService.loading$;
  readonly total$ = this.searchService.total$;
  canAdd$ = of(false);
  canExport$ = of(false);

  ngOnInit(): void {
    this.searchService.init();
    this.canAdd$ = this.auth.user.pipe(map(() => this.auth.can('healthcareService' as any, 'add')));
    this.canExport$ = this.auth.user.pipe(map(() => this.auth.can('healthcareService' as any, 'viewAll')));
  }

  ngOnDestroy(): void {
    this.searchService.destroy();
  }

  onSearch(criteria: { term: string; field: string | null }): void {
    this.searchService.search({ name: criteria.term });
  }

  onPageChange(event: PageEvent): void {
    this.searchService.changePage(event.pageIndex);
  }

  refresh(): void {
    if (this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    this.service.refreshFromServer().pipe(
      finalize(() => (this.isRefreshing = false))
    ).subscribe({
      next: () => {
        this.snackBar.open('Healthcare services refreshed', 'Close', { duration: 2500 });
        this.searchService.refresh();
      },
      error: () => this.errorService.openandCloseError('Unable to refresh healthcare services right now.')
    });
  }

  openCreateDialog(): void {
    const ref = this.dialog.open(HealthcareServiceEditorDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      panelClass: 'healthcare-service-editor-dialog',
      data: {}
    });
    ref.afterClosed().subscribe(result => {

      if (result) {
        //refresh data
        this.searchService.refresh();
        this.snackBar.open('Healthcare service saved', 'Close', { duration: 3000 });
      }
    });
  }

  openEditDialog(resource: HealthcareService): void {
    const ref = this.dialog.open(HealthcareServiceEditorDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      panelClass: 'healthcare-service-editor-dialog',
      data: { resource }
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Healthcare service updated', 'Close', { duration: 3000 });
      }
    });
  }

  delete(resource: HealthcareService): void {
    if (!resource?.id) {
      this.errorService.openandCloseError('Cannot delete a healthcare service without an id.');
      return;
    }
    if (!confirm(`Delete ${resource.name || 'this healthcare service'}?`)) {
      return;
    }
    this.service.deleteHealthcareService(resource).subscribe({
      next: () => {
        this.snackBar.open('Healthcare service deleted', 'Close', { duration: 2500 })
        this.searchService.refresh();
      },
      error: () => this.errorService.openandCloseError('Failed to delete healthcare service.')
    });
  }

  toggleActive(resource: HealthcareService, activate: boolean): void {
    if (!resource?.id) {
      this.errorService.openandCloseError('Cannot update a healthcare service without an id.');
      return;
    }
    if (!activate) {
      const confirmed = confirm(`Deactivate ${resource.name || 'this healthcare service'}?`);
      if (!confirmed) {
        return;
      }
    }
    this.service.setHealthcareServiceActive(resource, activate).subscribe({
      next: () => {
        const message = activate ? 'Healthcare service activated' : 'Healthcare service deactivated';
        this.snackBar.open(message, 'Close', { duration: 2500 });
        this.searchService.refresh();
      },
      error: () => this.errorService.openandCloseError('Unable to update service status right now.')
    });
  }

  trackById(_: number, resource: any): string | undefined {
    return resource.id;
  }
}

